/**
 * FileUploadButton — Upload files for chat messages.
 * Supports: images (JPEG/PNG/GIF), PDFs, and videos (MP4/WebM).
 * Max 50MB per file with drag-and-drop support.
 */
import React, { useRef, useState } from 'react';
import { uploadFile } from '../../api/fileStorage';
import type { UploadResult } from '../../api/fileStorage';

const ACCEPTED_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'application/pdf': ['.pdf'],
  'video/mp4': ['.mp4'],
  'video/webm': ['.webm'],
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ACCEPTED_MIME_TYPES = Object.keys(ACCEPTED_TYPES);

export interface FileUpload {
  file: File;
  fileKey?: string;
  error?: string;
  uploadedResult?: UploadResult;
}

interface FileUploadButtonProps {
  onFilesSelected: (uploads: FileUpload[]) => void;
  disabled?: boolean;
  conversationId: string;
}

export default function FileUploadButton({
  onFilesSelected,
  disabled = false,
  conversationId,
}: FileUploadButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const validateFiles = (files: FileList | File[]): FileUpload[] => {
    const uploads: FileUpload[] = [];

    Array.from(files).forEach((file) => {
      const upload: FileUpload = { file };

      // Check MIME type
      if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
        upload.error = `Unsupported file type: ${file.type}`;
      }
      // Check file size
      else if (file.size > MAX_FILE_SIZE) {
        upload.error = `File too large. Max 50MB, got ${(file.size / 1024 / 1024).toFixed(1)}MB`;
      }

      uploads.push(upload);
    });

    return uploads;
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    const uploads = validateFiles(files);
    onFilesSelected(uploads);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.currentTarget.files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_MIME_TYPES.join(',')}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        title="Upload files (drag and drop supported)"
        style={{
          padding: '4px 8px',
          background: 'none',
          border: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: 'var(--text-secondary)',
          fontSize: 16,
          opacity: disabled ? 0.5 : 1,
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.color = 'var(--text-primary)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'var(--text-secondary)';
        }}
      >
        📎
      </button>

      {/* Drag-and-drop overlay */}
      {isDragging && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            pointerEvents: 'auto',
          }}
        >
          <div
            style={{
              padding: '32px',
              background: 'var(--bg-elevated)',
              borderRadius: 'var(--radius-lg)',
              border: '2px dashed var(--border-strong)',
              textAlign: 'center',
              color: 'var(--text-primary)',
            }}
          >
            <div style={{ fontSize: 32, marginBlockEnd: 8 }}>📁</div>
            <div>Drop files here to upload</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBlockStart: 8 }}>
              Images, PDFs, or videos (max 50MB each)
            </div>
          </div>
        </div>
      )}
    </>
  );
}
